import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { api, friendlyError } from '@/lib/api';
import { ROUTES } from '@/lib/constants';
import { AUTH } from '@/constants/testIds';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setMessage(null);
    if (!token) {
      setError('Token no proporcionado. Solicita un nuevo enlace de recuperación.');
      return;
    }
    if (password !== confirmation) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/auth/reset-password', {
        token,
        password,
        password_confirmation: confirmation,
      });
      setMessage(res?.data?.message || 'Contraseña actualizada correctamente.');
      setTimeout(() => navigate(ROUTES.login), 1800);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Nueva contraseña"
      subtitle="Crea una contraseña segura para tu cuenta."
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password" className="text-sm font-medium">Nueva contraseña</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid={AUTH.resetPassword}
            className="h-11 bg-white"
          />
          <p className="text-[11px] text-[var(--ns-text-muted)] leading-snug">
            Mín. 8 caracteres, con mayúscula, minúscula y número.
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirmation" className="text-sm font-medium">Confirmar contraseña</Label>
          <Input
            id="confirmation"
            type="password"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            data-testid={AUTH.resetPasswordConfirmation}
            className="h-11 bg-white"
          />
        </div>

        {message && (
          <Alert
            data-testid={AUTH.resetMessage}
            className="border-[var(--ns-green)]/40 bg-[var(--ns-green)]/8"
          >
            <AlertDescription className="text-[var(--ns-green-700)]">{message}</AlertDescription>
          </Alert>
        )}
        {error && (
          <Alert variant="destructive" data-testid={AUTH.resetError}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          disabled={submitting}
          data-testid={AUTH.resetSubmit}
          className="h-11 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] border-0 shadow-[var(--ns-shadow-gold)] transition-all"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar contraseña'}
        </Button>

        <p className="text-sm text-[var(--ns-text-muted)] text-center">
          <Link
            to={ROUTES.login}
            className="text-[var(--ns-gold-600)] hover:text-[var(--ns-gold-700)] font-semibold hover:underline"
          >
            Volver al inicio de sesión
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
